import { useLocation, useNavigate, useParams } from "react-router-dom";
import { useEffect, useRef, useState } from "react";
import { HttpStatusCode } from "axios";
import clsx from "clsx";

import useErrors from "../../../../libs/useErrors";
import {
  isNotNull,
  makeSpaceDefaultPrice,
} from "../../../../libs/spaceReservation";
import {
  getDateString,
  getDateWithDayOfWeekString,
  getMaxMonthDate,
  getTimeString,
  isSameDate,
} from "../../../../libs/dateFormat";
import {
  convertToKoreanNumber,
  formatPhoneNumber,
} from "../../../../libs/numberFormat";
import { cleanTermContent } from "../../../../libs/term/term";

import { KeyValue } from "../../../../types/keyValue";

import { useUserContext } from "../../../../contexts/userContext";

import {
  AvailableDays,
  DEFAULT_WEEK_DAYS,
  RentalDevice,
  SpaceReservationCreateRequest,
  SpaceReservationItem,
  createSpaceReservation,
  getSpaceReservationDetail,
} from "../../../../services/spaceReservationServices";

import useFullScreenLoading from "../../../../hooks/useFullScreenLoading";
import { AgreementRightArrow } from "../../../../hooks/useAgreement";

import StickyHeader from "../../../../components/Layout/StickyHeader";
import CommonTextArea from "../../../../components/TextArea";
import CommonInput from "../../../../components/Input/CommonInput";
import CommonLabel from "../../../../components/Label";
import CheckWithLabel from "../../../../components/Input/CheckWithLabel";
import HorizontalRule from "../../../../components/Rule/Horizontal";
import RadioButtonGroup from "../../../../components/Input/RadioButtonGroup";
import CommonButton from "../../../../components/Button";
import CommonPopup from "../../../../components/Popup";
import CustomDatePicker from "../../../../components/Input/DatePicker";
import TimeSlots, {
  addMinutes,
  getRangeMinutes,
  getTimeCount,
  timeCompare,
  timeToMinutes,
} from "../../../../components/SpaceReservation/TimeSlots";
import { ReservationDetailWrapper } from "../Detail";
import SimplePopup from "../../../../components/Popup/Simple";
import ReservationLimitPopup from "../../../../components/Popup/ReservationLimit";
import BottomButtonGroup from "../../../../components/Button/Bottom";
import BottomGroup from "../../../../components/Button/Group";
import {
  RESERVATION_CLEAN_TIME_MINUTE,
  RESERVATION_SHOWER_ROOM_TODAY_GAP_MINUTE,
  RESERVATION_SPACE_TYPES,
  convertReservationTypeToLabel,
  reservationSpaceType,
} from "../../../../types/reservation";

type Props = {
  testReservationDate?: Date;
};
export default function SpaceReservationCreate({ testReservationDate }: Props) {


  const reservationDateRef = useRef<HTMLInputElement>(null);
  const purposeRef = useRef<HTMLTextAreaElement>(null);
  const userCountRef = useRef<HTMLInputElement>(null);
  const actualUserNameRef = useRef<HTMLInputElement>(null);
  const usedDepartmentRef = useRef<HTMLInputElement>(null);
  const userTelRef = useRef<HTMLInputElement>(null);
  const agreementRef = useRef<HTMLInputElement>(null);
  const location = useLocation();

  const navigate = useNavigate();
  const [errors, { removeError, setError }] = useErrors();
  const [user] = useUserContext();
  const params = useParams();
  const [detail, setDetail] = useState<Partial<SpaceReservationItem>>({});
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const loadingComponent = useFullScreenLoading({ isLoading });
  const [reservationTimeStart, setReservationTimeStart] = useState<string>("");
  const [reservationTimeEnd, setReservationTimeEnd] = useState<string>("");
  const [reservation, setReservation] = useState<
    Partial<SpaceReservationCreateRequest>
  >({});
  const [popup, setPopup] = useState<string>("");

  const [selectedRentalDeviceList, setSelectedRentalDeviceList] = useState<
    RentalDevice[]
  >([]);

  // testReservationDate 는 테스트에서만 존재 
  const isTest = testReservationDate !== undefined;

  const selectedIsSelfReservationItemOption = [
    { key: "true", value: "본인 사용" },
    { key: "false", value: "다른 사람 사용" },
  ];

  const [selectedIsSelfReservationItem, setSelectedIsSelfReservationItem] =
    useState<KeyValue>(selectedIsSelfReservationItemOption[0]);

  const [totalDevicePrice, setTotalDevicePrice] = useState<number>(0);
  const [totalSpaceHourText, setTotalSpaceHourText] = useState<string>("");
  const [totalSpacePriceText, setTotalSpacePriceText] =
    useState<React.ReactNode>("");

  const [agreement, setAgreement] = useState<boolean>(false);

  const [errorMsg, setErrorMsg] = useState<string>("");

  const selectedDate = new Date(location?.state?.date || Date.now());

  function getNextWorkday(
    date: Date,
    availableDays: AvailableDays,
    usePayment: boolean = false,
    reservationSpaceType: reservationSpaceType
  ): Date {
    const daysOfWeek: (keyof AvailableDays)[] = [
      "sunday",
      "monday",
      "tuesday",
      "wednesday",
      "thursday",
      "friday",
      "saturday",
    ];
    const day = date.getDay(); // 현재 요일 가져오기 (0: 일요일, 1: 월요일, ..., 6: 토요일)

    let nextDay =
      (day +
        (usePayment === true &&
        isSameDate(date, new Date()) &&
        reservationSpaceType !== RESERVATION_SPACE_TYPES.SHOWER
          ? 1
          : 0)) %
      7; // 유료 & 당일 & 샤워실이 아닌경우 -> 다음 날짜
    if (availableDays) {
      // if 문으로 체크를 안하면 테스트 시 넘어가지 않음. 로직상 필수인 요소이므로 조건문을 추가
      while (!availableDays[daysOfWeek[nextDay]]) {
        nextDay = (nextDay + 1) % 7; // 다음 가능한 날 찾기
      }
    }
    date.setDate(
      date.getDate() + (nextDay - day < 0 ? 7 : 0) + (nextDay - day)
    );
    return date;
  }

  const [reservationDate, setReservationDate] = useState<Date | null>(null);

  const [maxReservationDate, setMaxReservationDate] = useState<Date>(
    getMaxMonthDate()
  );

  const [nextWorkday, setNextWorkday] = useState<Date>(
    getNextWorkday(
      selectedDate,
      detail.availableDays ?? DEFAULT_WEEK_DAYS,
      detail.usePayment ?? location.state?.usePayment,
      detail.reservationSpaceType ?? location.state?.reservationSpaceType
    )
  );

  const PURPOSE_MIN_LENGTH = 2;

  function formatKoreanPhoneNumber(number: string | undefined) {
    if (number === undefined) return number;
    if (!/^\d{9,11}$/.test(number)) {
      return number;
    }

    // 서울 지역번호
    if (number.length === 9 && number.startsWith("02")) {
      return `${number.substring(0, 2)}-${number.substring(
        2,
        5
      )}-${number.substring(5, 9)}`;
    }

    // 기타 지역번호
    else if (
      number.length === 10 &&
      number.startsWith("0") &&
      !number.startsWith("010")
    ) {
      return `${number.substring(0, 3)}-${number.substring(
        3,
        6
      )}-${number.substring(6, 10)}`;
    }

    // 휴대전화 번호
    else if (number.length === 11) {
      return `${number.substring(0, 3)}-${number.substring(
        3,
        7
      )}-${number.substring(7, 11)}`;
    }

    return number;
  }

  const loadDetail = async (loadDate?: Date) => {
    setIsLoading(true);
    if (!params.id && !isTest) return;

    let reservationSpaceIdValue: string;
    isTest
      ? (reservationSpaceIdValue = "testId")
      : (reservationSpaceIdValue = params.id as string);

    try {
      const { status, data } = await getSpaceReservationDetail({
        reservationSpaceId: reservationSpaceIdValue,
        reservationDate: getDateString(
          loadDate ?? reservationDate ?? nextWorkday ?? new Date()
        ),
      });
      if (status === HttpStatusCode.Ok) {
        setDetail(data);
      }
    } catch (e: any) {
      console.log("SpaceReservationCreate loadDetail catch", e);
    } finally {
      setIsLoading(false);
    }
  };

  const makeSpacePrice = (detail: Partial<SpaceReservationItem>) => {
    let { totalSpaceDefaultPrice, totalSpacePriceText, totalSpaceHourText } =
      makeSpaceDefaultPrice(detail, reservation);

    setTotalSpacePriceText(totalSpacePriceText);
    setTotalSpaceHourText(totalSpaceHourText);

    setReservation((prev) => ({
      ...prev,
      totalSpaceDefaultPrice: String(totalSpaceDefaultPrice),
    }));
  };

  const validation = () => {
    if (
      detail.availablePeopleCountStart === undefined ||
      detail.availablePeopleCountEnd === undefined
    )
      return false;

    if (
      reservation.isSelfReservation === true ||
      reservation.isSelfReservation === undefined
    ) {
      reservation.isSelfReservation = true;
      reservation.actualUserName = user.name;
      reservation.userTel =
        formatPhoneNumber(user?.mobileNumber ?? "01022222222") ??
        "010-2222-2222";
    }

    // console.log(
    //   isNotNull(reservation),
    //   isNotNull(reservation.reservationDate),
    //   isNotNull(reservation.reservationTimeStart),
    //   isNotNull(reservation.reservationTimeEnd),
    //   isNotNull(reservation.purpose),
    //   isNotNull(reservation.userCount),
    //   Number(reservation.userCount) > detail.availablePeopleCountStart,
    //   Number(reservation.userCount) < detail.availablePeopleCountEnd,
    //   isNotNull(reservation.usedDepartment),
    //   isNotNull(reservation.rentalDeviceList),
    //   agreement,
    //   (reservation.isSelfReservation === false &&
    //     isNotNull(reservation.actualUserName) &&
    //     isNotNull(reservation.userTel)) ||
    //     reservation.isSelfReservation === true
    // );

    if (detail.usePayment && detail.usePayment === true) {
      if (!isNotNull(reservation.purpose)) return false;
      if ((reservation.purpose?.length ?? 0) < PURPOSE_MIN_LENGTH) return false;
      if (!isNotNull(reservation.usedDepartment)) return false;
    }

    return (
      isNotNull(reservation) &&
      isNotNull(reservation.reservationDate) &&
      isNotNull(reservation.reservationTimeStart) &&
      isNotNull(reservation.reservationTimeEnd) &&
      isNotNull(reservation.userCount) &&
      Number(reservation.userCount) >= detail.availablePeopleCountStart &&
      Number(reservation.userCount) <= detail.availablePeopleCountEnd &&
      isNotNull(reservation.rentalDeviceList) &&
      agreement &&
      ((reservation.isSelfReservation === false &&
        isNotNull(reservation.actualUserName) &&
        isNotNull(reservation.userTel)) ||
        reservation.isSelfReservation === true)
    );
  };

  const checkDailyLimit = () => {
    if (detail.reservationList === undefined) return true;
    if (detail.reservationList.length === 0) return true;
    if (detail.dailyLimit === undefined) return true;
    if (detail.usePayment === undefined || detail.usePayment === true)
      return true;
    if (
      detail.dailyLimit !== undefined &&
      detail.reservationList &&
      user.memberId
    ) {
      if (
        detail.reservationList.filter(
          (reservation) => reservation.createdBy === user.memberId
        ).length <= detail.dailyLimit
      ) {
        return true;
      }
    }
    return false;
  };

  const handleConfirmDatetime = (e: any) => {
    const range = getRangeMinutes(reservationTimeStart, reservationTimeEnd);
    if (checkDailyLimit() === false) {
      setPopup("dailyLimit");
    }

    if (
      detail.usableTimeEnd === undefined ||
      detail.usableTimeStart === undefined ||
      detail.usePayment === undefined
    )
      return;

    // 샤워룸, 당일 일때 일 때 현재 시간 + 90분보다 이전 시간에 예약하려고 하면 오류 처리
    // 추가 - 샤워실 + 자동승인이면 90분 더한거 체크하지않음
    if (
      detail.reservationSpaceType === RESERVATION_SPACE_TYPES.SHOWER &&
      !detail.useAutoConfirm &&
      (reservationDate || nextWorkday)
    ) {
      const currentTime = new Date();
      const currentSlotTime = addMinutes(
        `${String(currentTime.getHours()).padStart(2, "0")}:${String(
          currentTime.getMinutes()
        ).padStart(2, "0")}`,
        RESERVATION_SHOWER_ROOM_TODAY_GAP_MINUTE
      );
      const isSameData = isSameDate(reservationDate ?? nextWorkday, new Date());
      if (isSameData) {
        if (timeCompare(currentSlotTime, reservationTimeStart, true)) {
          setPopup("reservationShowerRoomDateTimeFail");
          setError(
            "reservationDateTime",
            "현재 시간으로부터 90분 이후에만 예약 가능합니다."
          );
          return;
        }
      }
    }

    //무료인 경우 최소 시간 비교
    if (
      (range < detail.usableTimeStart || range > detail.usableTimeEnd) &&
      !detail.usePayment
    ) {
      setError(
        "reservationDateTime",
        `예약 가능 시간(최소 ${detail.usableTimeStart || "-"}분, 최대 ${
          detail.usableTimeEnd || "-"
        }분)을 확인해주세요`
      );
      setPopup("reservationDateTimeFail");
      return;
    } else if (range < detail.usableTimeStart && detail.usePayment) {
      setError(
        "reservationDateTime",
        `예약 가능 시간(최소 ${detail.usableTimeStart || "-"}분)을 확인해주세요`
      );
      setPopup("reservationDateTimeFail");
      return;
    }

    if (reservationTimeStart === "" || reservationTimeEnd === "") {
      setPopup("datetimeError");
    } else {
      setReservation((prev) => ({
        ...prev,
        reservationDate: getDateString(reservationDate ?? nextWorkday),
        reservationTimeStart: reservationTimeStart,
        reservationTimeEnd: reservationTimeEnd,
      }));
      setPopup("");
    }
  };

  const handleConfirm = (e: any) => {
    e.preventDefault();
    if (
      detail.availablePeopleCountStart === undefined ||
      detail.availablePeopleCountEnd === undefined
    )
      return;

    if (
      !isNotNull(reservation.reservationDate) ||
      !isNotNull(reservation.reservationTimeStart) ||
      !isNotNull(reservation.reservationTimeEnd)
    ) {
      setError("reservationDate", `예약 날짜와 이용시간을 선택해주세요`);
      reservationDateRef.current?.focus();
      return false;
    }

    const purposeLength = reservation.purpose?.length ?? 0;
    if (purposeLength < PURPOSE_MIN_LENGTH && detail.usePayment) {
      const message =
        purposeLength === 0
          ? `이용 목적을 입력해주세요`
          : `이용 목적을 ${PURPOSE_MIN_LENGTH}자 이상 입력해주세요`;

      setError("purpose", message);
      purposeRef.current?.focus();
      return false;
    }
    if (
      isNaN(Number(reservation.userCount)) ||
      Number(reservation.userCount) < detail.availablePeopleCountStart ||
      Number(reservation.userCount) > detail.availablePeopleCountEnd
    ) {
      setError(
        "userCount",
        `사용자 수가 범위를 벗어났습니다. (최소 ${
          detail.availablePeopleCountStart || "-"
        }명, 최대 ${detail.availablePeopleCountEnd || "-"}명)`
      );
      userCountRef.current?.focus();
      return false;
    }

    if (reservation.isSelfReservation === false) {
      if (!isNotNull(reservation.actualUserName)) {
        setError("actualUserName", `실 사용자를 입력해주세요`);
        actualUserNameRef.current?.focus();
        return false;
      }
      if (!isNotNull(reservation.userTel)) {
        setError("userTel", `실 사용자 연락처를 입력해주세요`);
        userTelRef.current?.focus();
        return false;
      }
    }

    if (!isNotNull(reservation.usedDepartment) && detail.usePayment) {
      setError("usedDepartment", `사용 부서를 입력해주세요`);
      usedDepartmentRef.current?.focus();
      return false;
    }

    if (!agreement) {
      setError("agreement", `약관에 동의해주세요`);
      agreementRef.current?.focus();
      return false;
    }

    setPopup("confirm");
  };

  const handleConfirmReservation = async () => {
    if (!validation()) return;

    if (!params.id) return;
    if (!reservation.reservationDate) return;
    if (!reservation.reservationTimeStart) return;
    if (!reservation.reservationTimeEnd) return;
    if (!reservation.purpose && detail.usePayment) return;
    if (!reservation.userCount) return;
    if (!reservation.usedDepartment && detail.usePayment) return;
    if (!reservation.totalSpaceDefaultPrice) return;
    if (!reservation.totalDeviceDefaultPrice) return;
    if (!reservation.vatDefaultPrice) return;
    if (reservation.isSelfReservation === undefined)
      reservation.isSelfReservation = true;

    const isShowerRoom =
      detail.reservationSpaceType === RESERVATION_SPACE_TYPES.SHOWER;
    let beforeCleanTime: string;
    let afterCleanTime: string;
    if (isShowerRoom) {
      beforeCleanTime = reservation.reservationTimeStart;
      afterCleanTime = reservation.reservationTimeEnd;
    } else {
      beforeCleanTime =
        reservation.reservationTimeStart === "00:00"
          ? reservation.reservationTimeStart
          : getTimeString(
              timeToMinutes(reservation.reservationTimeStart) -
                RESERVATION_CLEAN_TIME_MINUTE
            );
      afterCleanTime =
        reservation.reservationTimeEnd === "24:00"
          ? reservation.reservationTimeEnd
          : getTimeString(
              timeToMinutes(reservation.reservationTimeEnd) +
                RESERVATION_CLEAN_TIME_MINUTE
            );
    }

    const param = {
      reservationSpaceId: params.id,
      reservationDate: reservation.reservationDate,
      reservationTimeStart: reservation.reservationTimeStart,
      reservationTimeEnd: reservation.reservationTimeEnd,
      beforeCleanTime: beforeCleanTime,
      afterCleanTime: afterCleanTime,
      purpose: reservation.purpose,
      userCount: reservation.userCount,
      totalSpaceDefaultPrice: reservation.totalSpaceDefaultPrice,
      totalDeviceDefaultPrice: reservation.totalDeviceDefaultPrice,
      vatDefaultPrice: reservation.vatDefaultPrice,
      isSelfReservation: reservation.isSelfReservation,
      actualUserName: reservation.actualUserName,
      usedDepartment: reservation.usedDepartment,
      userTel: reservation.userTel,
      rentalDeviceList: reservation.rentalDeviceList ?? [],
    };
    // console.log(param);

    setIsLoading(true);
    try {
      const { status, data } = await createSpaceReservation(param);

      setErrorMsg("");
      if (status === HttpStatusCode.Ok) {
        if (data.reservationStatus === "CONFIRMED") {
          navigate(
            `/service/space-reservation/confirmed/${data.spaceReservationDetailId}`
          );
        } else {
          navigate(
            `/service/space-reservation/waiting/${data.spaceReservationDetailId}`
          );
        }
      }
    } catch (error: any) {
      if (error?.response?.data?.code === -605) {
        setPopup("reservationLimit");
      } else {
        const errorMsg = error.response.data.error.split("-").pop().trim();

        console.log(errorMsg);
        setErrorMsg(errorMsg);

        setPopup("createFail");
      }
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    const result = getNextWorkday(
      selectedDate,
      detail.availableDays ?? DEFAULT_WEEK_DAYS,
      detail.usePayment ?? location.state?.usePayment,
      detail.reservationSpaceType ?? location.state?.reservationSpaceType
    );

    console.log("useEffect - result:", result);
    if (!isSameDate(result, nextWorkday)) {
      setNextWorkday(result);

      if (reservationDate === null) {
        loadDetail(result);
        setReservationDate(result);
      }
    }
  }, [detail.availableDays, detail.usePayment, location.state?.usePayment]);

  useEffect(() => {
    if (isTest) loadDetail();
    if (user.isLoading) return;
    loadDetail();
  }, [reservationDate, user.isLoading, testReservationDate]);

  //  디바이스 할증
  useEffect(() => {
    if (detail.usePayment === true) {
      let range = 1;
      if (reservation.reservationTimeStart && reservation.reservationTimeEnd) {
        range = getTimeCount(
          reservation?.reservationTimeStart,
          reservation?.reservationTimeEnd
        );
      }
      setReservation((prev) => ({
        ...prev,
        rentalDeviceList: selectedRentalDeviceList.map(
          (device) => device.rentalDeviceId
        ),
        totalDeviceDefaultPrice: String(Math.round(totalDevicePrice * range)),
      }));
    } else {
      setReservation((prev) => ({
        ...prev,
        rentalDeviceList: selectedRentalDeviceList.map(
          (device) => device.rentalDeviceId
        ),
        totalDeviceDefaultPrice: "0",
      }));
    }
  }, [
    detail.usePayment,
    selectedRentalDeviceList,
    totalDevicePrice,
    reservation.reservationTimeStart,
    reservation.reservationTimeEnd,
  ]);

  useEffect(() => {
    const price =
      Number(reservation.totalSpaceDefaultPrice) +
      Number(reservation.totalDeviceDefaultPrice);

    setReservation((prev) => ({
      ...prev,
      vatDefaultPrice: String(Math.round(price * 0.1)),
    }));
  }, [reservation.totalSpaceDefaultPrice, reservation.totalDeviceDefaultPrice]);

  useEffect(() => {
    makeSpacePrice(detail);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    reservation.reservationDate,
    reservation.reservationTimeStart,
    reservation.reservationTimeEnd,
  ]);

  useEffect(() => {
    if (
      reservation.isSelfReservation === undefined ||
      reservation.isSelfReservation === true
    ) {
      setReservation((prev) => ({
        ...prev,
        actualUserName: user.name,
        userTel:
          formatPhoneNumber(user?.mobileNumber ?? "01022222222") ??
          "010-2222-2222",
      }));
    } else {
      setReservation((prev) => ({
        ...prev,
        actualUserName: "",
        userTel: "",
      }));
    }
  }, [user?.mobileNumber, reservation.isSelfReservation, user.name]);

  return (
    <>
      {loadingComponent}
      <div className="px-5 mb-14">
        <StickyHeader
          title="공간 예약"
          onBackClick={() =>
            navigate(
              location?.state?.parentUrl ?? "/service/space-reservation",
              {
                state: {
                  parentState: location?.state?.parentState,
                  ...location?.state?.parentState,
                },
              }
            )
          }
        />
        <div className="flex flex-col text-3xl pt-5 gap-2.5 leading-[38px] tracking-[-1.2px] font-light">
          <div className="pb-1.5">
            <div className="font-medium">{detail.buildingName || ""}</div>
            <div className="">
              <span className="font-medium">
                {detail.buildingFloorName || ""}{" "}
                {detail.reservationSpaceName || ""}
              </span>
              &nbsp;에서
            </div>
          </div>
          <div
            tabIndex={-1}
            ref={reservationDateRef}
            className="flex flex-row cursor-pointer"
            onClick={() => {
              if (detail.usePayment === true && reservationDate !== null) {
                if (isSameDate(reservationDate, new Date()) === true) {
                  setReservationDate(nextWorkday);
                }
              }
              removeError("reservationDate");
              setPopup("datetime");
            }}
          >
            {!isNotNull(reservation.reservationDate) ? (
              <div
                className={clsx([
                  "underline",
                  {
                    "font-bold text-brand-neutral-20":
                      !errors["reservationDate"],
                  },
                  {
                    "text-brand-primary-magenta opacity-80":
                      errors["reservationDate"],
                  },
                ])}
              >
                예약 날짜 선택
              </div>
            ) : (
              <div className="underline text-bold">
                {getDateWithDayOfWeekString(
                  new Date(reservation?.reservationDate ?? nextWorkday)
                )}
              </div>
            )}
          </div>
          <div
            className="flex flex-row pb-5 flex-wrap gap-1.5"
            onClick={() => {
              removeError("reservationDate");
              setPopup("datetime");
            }}
          >
            {!isNotNull(reservation.reservationTimeStart) &&
            !isNotNull(reservation.reservationTimeEnd) ? (
              <div
                className={clsx([
                  "underline",
                  {
                    "font-bold text-brand-neutral-20":
                      !errors["reservationDate"],
                  },
                  {
                    "text-brand-primary-magenta opacity-80":
                      errors["reservationDate"],
                  },
                ])}
              >
                이용 시간 선택
              </div>
            ) : (
              <div className="underline text-bold">
                {reservation.reservationTimeStart} ~{" "}
                {reservation.reservationTimeEnd}
              </div>
            )}
            이용할게요
            {errors["reservationDate"] && (
              <div
                className="px-4 pt-1 text-brand-primary-magenta text-xs"
                aria-label="errorMessage"
              >
                {errors["reservationDate"]}
              </div>
            )}
          </div>
        </div>
        {/* 2025-04-29 famous LG서웅역빌딩 일경우 예약개시 48시간 이내 예약 취소 시 취소 수수료(100%)가 부과됩니다 문구 삭제 TG_0000856 */}
        {detail?.usePayment === true &&
          detail.reservationSpaceType !== RESERVATION_SPACE_TYPES.SHOWER && detail.buildingId !== "TG_0000856" && (
            <div className="py-[33px]">
              <div className="flex flex-col py-5 px-2.5 items-center justify-center bg-[#F7F7F7] text-center text-sm font-normal	leading-4">
                예약개시 48시간 이내 예약 취소 시 <br />
                취소 수수료(100%)가 부과됩니다
              </div>
            </div>
          )}
        <ReservationLabelWrapper
          label="이용 목적"
          required={detail?.usePayment}
        >
          <CommonTextArea
            ref={purposeRef}
            className="w-full"
            placeholder={`이용 목적을 ${PURPOSE_MIN_LENGTH}자 이상 입력해주세요`}
            value={reservation.purpose}
            error={errors["purpose"]}
            minLength={PURPOSE_MIN_LENGTH}
            onChange={(e) => {
              removeError("purpose");
              setReservation((prev) => ({
                ...prev,
                purpose: e.target.value,
              }));
            }}
          />
        </ReservationLabelWrapper>
        <ReservationLabelWrapper label="사용자 수">
          <CommonInput
            type="number"
            className="w-full"
            placeholder={`사용자 수를 입력해주세요 (최소 ${
              detail.availablePeopleCountStart || "-"
            }명, 최대 ${detail.availablePeopleCountEnd || "-"}명)`}
            error={errors["userCount"]}
            min={detail.availablePeopleCountStart}
            max={detail.availablePeopleCountEnd}
            minLength={10}
            value={reservation?.userCount ?? ""}
            onChange={(e) => {
              removeError("userCount");
              setReservation((prev) => ({
                ...prev,
                userCount: e.replace(/\D/g, ""),
              }));
            }}
            pattern="[0-9]*"
            ref={userCountRef}
          />
        </ReservationLabelWrapper>
        {detail?.usePayment && (
          <ReservationLabelWrapper label="실제 사용 여부">
            <RadioButtonGroup
              className="w-full !text-sm"
              variantActive="select"
              variantInactive="secondary"
              items={selectedIsSelfReservationItemOption}
              value={
                selectedIsSelfReservationItem ??
                selectedIsSelfReservationItemOption[0]
              }
              onChange={(e) => {
                setSelectedIsSelfReservationItem(e);
                if (e.key === "true") {
                  removeError("actualUserName");
                }
                setReservation((prev) => ({
                  ...prev,
                  isSelfReservation: e.key === "true" ? true : false,
                }));
              }}
            />
          </ReservationLabelWrapper>
        )}
        {reservation?.isSelfReservation === false && (
          <ReservationLabelWrapper label="실 사용자명">
            <CommonInput
              className="w-full"
              placeholder="실 사용자명을 입력해주세요"
              value={reservation?.actualUserName ?? ""}
              error={errors["actualUserName"]}
              onChange={(e) => {
                removeError("actualUserName");
                setReservation((prev) => ({
                  ...prev,
                  actualUserName: e,
                }));
              }}
              minLength={50}
              ref={actualUserNameRef}
            />
          </ReservationLabelWrapper>
        )}
        {reservation?.isSelfReservation === false && (
          <ReservationLabelWrapper label="실 사용자 연락처">
            <CommonInput
              type="tel"
              className="w-full"
              placeholder="실 사용자 연락처를 입력해주세요"
              error={errors["userTel"]}
              value={reservation?.userTel ?? ""}
              maxLength={13}
              onChange={(e) => {
                removeError("userTel");
                setReservation((prev) => ({
                  ...prev,
                  userTel: formatKoreanPhoneNumber(e),
                }));
              }}
              pattern="[0-9-]*"
              minLength={5}
              ref={userTelRef}
            />
          </ReservationLabelWrapper>
        )}
        <ReservationLabelWrapper
          label="사용 부서"
          required={detail?.usePayment}
        >
          <CommonInput
            className="w-full"
            placeholder="사용 부서를 입력해주세요"
            error={errors["usedDepartment"]}
            value={reservation?.usedDepartment ?? ""}
            onChange={(e) => {
              removeError("usedDepartment");
              setReservation((prev) => ({
                ...prev,
                usedDepartment: e,
              }));
            }}
            minLength={50}
            ref={usedDepartmentRef}
          />
        </ReservationLabelWrapper>

        {detail.rentalDeviceList?.length !== 0 && (
          <ReservationLabelWrapper label="사용 가능 기기" required={false}>
            <div
              className="flex flex-col py-5 px-2 bg-[#F7F7F7] text-base gap-2.5"
              tabIndex={-1}
            >
              {detail.rentalDeviceList?.map((device, index) => (
                <div key={device.rentalDeviceId} className="flex flex-row">
                  <CheckWithLabel
                    checked={
                      selectedRentalDeviceList.find(
                        (d) => d.rentalDeviceId === device.rentalDeviceId
                      ) !== undefined
                    }
                    labelClassName="w-full"
                    label={
                      <div className="flex flex-row gap-10 justify-between items-center">
                        <div className="flex flex-col">{device.deviceName}</div>
                        <div className="flex flex-col text-sm text-gray-600">
                          {detail?.usePayment
                            ? `30분 ${convertToKoreanNumber(device.price)}원`
                            : ""}
                        </div>
                      </div>
                    }
                    onChange={(e) => {
                      if (e) {
                        setSelectedRentalDeviceList((prev) => [
                          ...prev,
                          device,
                        ]);
                        if (detail.usePayment)
                          setTotalDevicePrice((prev) => prev + device.price);
                      } else {
                        setSelectedRentalDeviceList((prev) =>
                          prev.filter(
                            (d) => d.rentalDeviceId !== device.rentalDeviceId
                          )
                        );
                        if (detail.usePayment)
                          setTotalDevicePrice((prev) => prev - device.price);
                      }
                    }}
                  />
                </div>
              ))}
            </div>
          </ReservationLabelWrapper>
        )}
        <ReservationLabelWrapper label="사용 규정 동의">
          <HorizontalRule margin="0" />
          <div
            className="flex flex-row py-5 justify-between pr-2.5 gap-2.5"
            tabIndex={-1}
            ref={agreementRef}
            onClick={() => {
              removeError("agreement");
              setPopup("agreement");
            }}
          >
            <CheckWithLabel
              checked={agreement}
              label={
                <div className="cursor-pointer text-base font-normal leading-[19px]">
                  예약 공간 사용 규정&nbsp;
                  <span className="text-red-500">(필수)</span>
                </div>
              }
              onChange={(e) => {
                if (agreement) {
                  setAgreement(false);
                }
              }}
            />
            <AgreementRightArrow />
          </div>
          <HorizontalRule margin="0" />
          {errors["agreement"] && (
            <div
              className="px-4 pt-1 text-brand-primary-magenta text-xs"
              aria-label="errorMessage"
            >
              {errors["agreement"]}
            </div>
          )}
        </ReservationLabelWrapper>

        <div className="flex flex-row mb-10 px-2.5 pt-10 pb-5 text-2xl items-center font-medium justify-between">
          <div className="flex text-lg leading-[21px]">총 결제 예정 금액</div>
          <div className="flex text-3xl leading-[17px]">
            <strong>
              {Number(reservation.totalSpaceDefaultPrice ?? 0) +
                Number(reservation.totalDeviceDefaultPrice ?? 0) ===
              0
                ? detail?.usePayment
                  ? "0 원"
                  : "무료"
                : `${convertToKoreanNumber(
                    Number(reservation.totalSpaceDefaultPrice ?? 0) +
                      Number(reservation.totalDeviceDefaultPrice ?? 0)
                  )} 원`}
            </strong>
          </div>
        </div>
        <BottomButtonGroup
          validation={!validation()}
          title="예약하기"
          onClick={handleConfirm}
        />
      </div>

      <CommonPopup
        title="예약 날짜 선택"
        isOpen={popup === "datetime"}
        padding={0}
        unUseHorizonRule
        onClose={() => {
          if (
            reservation.reservationTimeStart &&
            reservation.reservationTimeEnd &&
            reservation.reservationDate
          ) {
            setReservationDate(new Date(reservation.reservationDate));
            setReservationTimeStart(reservation.reservationTimeStart);
            setReservationTimeEnd(reservation.reservationTimeEnd);
          } else {
            setReservationDate(null);
            setReservationTimeStart("");
            setReservationTimeEnd("");
          }
          setPopup("");
        }}
      >
        <div
          className="flex flex-col h-[65vh] px-5"
          data-testid={"reservationDatePickPopup"}
        >
          <ReservationLabelWrapper label="예약 날짜">
            <CustomDatePicker
              date={reservationDate ?? nextWorkday}
              wrapperClassName="w-full"
              inputType="date"
              dateFormat="yyyy-MM-dd"
              minDate={nextWorkday}
              maxDate={maxReservationDate}
              onChange={(date) => {
                setReservationDate(date);
                setReservationTimeStart("");
                setReservationTimeEnd("");
              }}
            />
          </ReservationLabelWrapper>
          <ReservationLabelWrapper label="예약 시간">
            <div className="flex flex-row gap-1.5 items-center justify-between">
              <div className="w-full">
                <CommonInput
                  disabled
                  placeholder="사용 시작 시간"
                  readOnly
                  value={reservationTimeStart ?? ""}
                  onChange={(e) => setReservationTimeStart(e)}
                />
              </div>
              ~
              <div className="w-full">
                <CommonInput
                  placeholder="사용 종료 시간"
                  disabled
                  readOnly
                  value={reservationTimeEnd ?? ""}
                  onChange={(e) => setReservationTimeEnd(e)}
                />
              </div>
            </div>
            {errors["reservationDateTime"] && (
              <div
                className="px-4 pt-1 text-brand-primary-magenta text-xs"
                aria-label="errorMessage"
              >
                {errors["reservationDateTime"]}
              </div>
            )}
          </ReservationLabelWrapper>

          <div className="py-5">
            <TimeSlots
              startTime={detail.availableTimeStart}
              endTime={detail.availableTimeEnd}
              useBlockTime={detail.useBlockTime as boolean}
              blockTimeStart={detail.blockTimeStart}
              blockTimeEnd={detail.blockTimeEnd}
              reservationSpaceType={
                detail.reservationSpaceType ?? RESERVATION_SPACE_TYPES.DEFAULT
              }
              reservationData={detail.reservationList ?? []}
              availableDays={detail.availableDays ?? DEFAULT_WEEK_DAYS}
              reservationDate={reservationDate ?? nextWorkday}
              reservationTimeStart={reservationTimeStart ?? ""}
              reservationTimeEnd={reservationTimeEnd ?? ""}
              availableTimeStart={detail.availableTimeStart ?? ""}
              availableTimeEnd={detail.availableTimeEnd ?? ""}
              usePayment={detail.usePayment ?? location.state?.usePayment}
              useAutoConfirm={detail.useAutoConfirm}
              onClickTimeSlot={() => {
                removeError("datetimeError");
                removeError("reservationDateTime");
              }}
              onSelectTimeRange={(startTime, endTime, error) => {
                if (error === "") {
                  if (checkDailyLimit() === true && detail.usableTimeEnd) {
                    if (
                      detail.usePayment !== true &&
                      getRangeMinutes(startTime, endTime) > detail.usableTimeEnd
                    ) {
                      setPopup("reservationDateTimeFail");
                    } else {
                      setReservationTimeStart(startTime);
                      setReservationTimeEnd(endTime);
                      removeError("datetimeError");
                    }
                  } else {
                    setPopup("dailyLimit");
                  }
                } else {
                  setError("datetimeError", error);
                  setPopup("datetimeError");
                }
              }}
            />
          </div>
          <div className="pb-[50px]"></div>
          <BottomButtonGroup
            title="확인"
            disabled={reservationTimeStart === "" || reservationTimeEnd === ""}
            variant={
              reservationTimeStart === "" || reservationTimeEnd === ""
                ? "grey"
                : "primary"
            }
            onClick={handleConfirmDatetime}
          />
        </div>
      </CommonPopup>
      <SimplePopup
        isOpen={popup === "reservationDateTimeFail"}
        message={
          detail.usePayment === true
            ? `예약 가능 시간(최소 ${
                detail.usableTimeStart || "-"
              }분)을 확인해주세요`
            : `예약 가능 시간(최소 ${detail.usableTimeStart || "-"}분, 최대 ${
                detail.usableTimeEnd || "-"
              }분)을 확인해주세요`
        }
        onClose={() => setPopup("datetime")}
      />
      <SimplePopup
        isOpen={popup === "reservationShowerRoomDateTimeFail"}
        message={"현재 시점에서 \n 90분 이후 부터 예약 가능합니다."}
        onClose={() => setPopup("datetime")}
      />
      <CommonPopup
        title="예약공간 사용 규정 (필수)"
        isOpen={popup === "agreement"}
        onClose={() => setPopup("")}
        padding={0}
        unUseHorizonRule
      >
        <div className="pb-12">
          <div className="max-h-[70vh] overflow-y-auto">
            {detail?.term ? (
              <div
                className="p-5"
                dangerouslySetInnerHTML={{
                  __html: cleanTermContent(detail.term) ?? "",
                }}
              ></div>
            ) : (
              <>
                <div className="p-5 pt-0 text-xl font-medium flex items-center justify-center h-[110px]">
                  약관을 찾을 수 없습니다.
                </div>
              </>
            )}
          </div>
          <BottomGroup className="flex justify-center">
            <CommonButton
              variant="primary"
              className="text-lg text-white px-4 py-[20px] w-full rounded-none h-bottom-button"
              onClick={() => {
                setAgreement(true);
                setPopup("");
              }}
            >
              확인
            </CommonButton>
          </BottomGroup>
        </div>
      </CommonPopup>
      <SimplePopup
        isOpen={popup === "createFail"}
        message={errorMsg}
        onClose={() => setPopup("confirm")}
      />
      <CommonPopup
        title=""
        isOpen={popup === "dailyLimit"}
        unUseHorizonRule
        padding={0}
        onClose={() => {
          setReservationTimeStart("");
          setReservationTimeEnd("");
          setPopup("datetime");
        }}
      >
        <div className="w-full p-5 text-center">
          예약 제한 기준을 초과했습니다
        </div>
        <CommonButton
          className="text-lg text-white mt-10 px-4 py-[20px] w-full"
          onClick={() => {
            setReservationTimeStart("");
            setReservationTimeEnd("");
            setPopup("datetime");
          }}
        >
          확인
        </CommonButton>
      </CommonPopup>

      <CommonPopup
        title=" "
        isOpen={popup === "datetimeError"}
        onClose={() => {
          setReservationTimeStart("");
          setReservationTimeEnd("");
          setPopup("datetime");
        }}
        unUseHorizonRule
        padding={0}
      >
        <div className="w-full p-5 text-center text-lg font-medium">
          {errors["datetimeError"] ?? "예약이 불가능한 시간입니다."}
        </div>
        <CommonButton
          className="text-lg text-white mt-5 px-4 py-[20px] w-full h-bottom-button rounded-none"
          onClick={() => {
            setReservationTimeStart("");
            setReservationTimeEnd("");
            setPopup("datetime");
          }}
        >
          확인
        </CommonButton>
      </CommonPopup>
      <CommonPopup
        title="예약 확인"
        isOpen={popup === "confirm"}
        padding={5}
        unUseHorizonRule
        onClose={() => setPopup("")}
      >
        <div className="flex flex-col max-h-[87vh]">
          <div className="pb-10">
            <div className="text-[26px] py-2 leading-[48px] tracking-[-1.04px] font-light">
              <span className="font-medium">예약정보</span>를 확인해주세요
              <HorizontalRule className="bg-black" thickness={3} />
            </div>
            <div className="text-[18px] font-medium leading-[21px] pt-8.5 pb-5">
              예약 정보
            </div>
            <ReservationDetailWrapper
              label="예약 공간"
              upHorizontalRule
              downHorizontalRule
            >
              <div className="">
                {detail.buildingName}&nbsp;
                {detail.buildingFloorName}
              </div>
              <div>{detail.reservationSpaceName}</div>
            </ReservationDetailWrapper>
            <ReservationDetailWrapper label="공간 분류" downHorizontalRule>
              {convertReservationTypeToLabel(
                detail.reservationSpaceType ?? "DEFAULT"
              )}
            </ReservationDetailWrapper>
            <ReservationDetailWrapper label="이용 시간" downHorizontalRule>
              {reservation?.reservationDate &&
                getDateWithDayOfWeekString(
                  new Date(reservation?.reservationDate ?? nextWorkday)
                )}
              <br />
              {reservation.reservationTimeStart} ~{" "}
              {reservation.reservationTimeEnd}
              <br />
              {totalSpaceHourText}
            </ReservationDetailWrapper>
            {reservation?.rentalDeviceList !== undefined &&
              reservation.rentalDeviceList.length > 0 && (
                <ReservationDetailWrapper label="기기 이용" downHorizontalRule>
                  {selectedRentalDeviceList.map((device, index) => (
                    <div
                      key={device.rentalDeviceId}
                      className="flex flex-row gap-2"
                    >
                      {device.deviceName}
                    </div>
                  ))}
                </ReservationDetailWrapper>
              )}
            <ReservationDetailWrapper label="예약자" downHorizontalRule>
              {user?.name}
            </ReservationDetailWrapper>
            {reservation.isSelfReservation === false && (
              <ReservationDetailWrapper label="실 사용자" downHorizontalRule>
                <div>{reservation.actualUserName}</div>
                <div>{reservation.userTel}</div>
              </ReservationDetailWrapper>
            )}
            <ReservationDetailWrapper label="이용 목적" downHorizontalRule>
              {reservation.purpose}
            </ReservationDetailWrapper>
            <div className="text-lg font-medium pt-[30px] leading-[21px] py-5">
              결제 정보
            </div>
            {detail.usePayment === true && (
              <>
                <ReservationDetailWrapper
                  label="공간 이용"
                  upHorizontalRule
                  downHorizontalRule
                >
                  {totalSpacePriceText}
                </ReservationDetailWrapper>
                {reservation?.rentalDeviceList !== undefined &&
                  reservation.rentalDeviceList.length > 0 && (
                    <ReservationDetailWrapper
                      label="기기 이용"
                      downHorizontalRule
                    >
                      {selectedRentalDeviceList.map((device, index) => (
                        <div
                          key={device.rentalDeviceId}
                          className="flex flex-row gap-2 text-base leading-[26px] font-normal"
                        >
                          {device.deviceName} :
                          <div className="font-semibold">
                            {convertToKoreanNumber(device.price)}
                          </div>
                          원
                        </div>
                      ))}
                    </ReservationDetailWrapper>
                  )}
              </>
            )}
            <ReservationDetailWrapper
              upHorizontalRule={!detail.usePayment}
              label={
                <div className="flex flex-col mt-[-12px] py-[15px]">
                  <div>결제 예정 총액</div>
                  <div>(VAT 포함)</div>
                </div>
              }
            >
              {detail.usePayment ? (
                <div className="flex text-[28px] leading-[17px] py-[15px]">
                  <span className="font-semibold">
                    {convertToKoreanNumber(
                      Number(reservation.vatDefaultPrice ?? 0) +
                        Number(reservation.totalSpaceDefaultPrice ?? 0) +
                        Number(reservation.totalDeviceDefaultPrice ?? 0)
                    )}
                  </span>
                  &nbsp;원
                </div>
              ) : (
                <div className="flex text-[28px] leading-[17px] py-[15px] font-semibold">
                  무료
                </div>
              )}
            </ReservationDetailWrapper>
          </div>
          <BottomGroup zIndex="high">
            <div className="flex flex-row">
              <CommonButton
                variant="grey"
                className="w-1/3 h-bottom-button rounded-none"
                onClick={() => {
                  setPopup("");
                }}
              >
                수정
              </CommonButton>
              <CommonButton
                disabled={isLoading}
                variant="primary"
                className="w-2/3 h-bottom-button rounded-none"
                onClick={handleConfirmReservation}
              >
                확인
              </CommonButton>
            </div>
          </BottomGroup>
        </div>
      </CommonPopup>

      <ReservationLimitPopup popup={popup} onClose={() => setPopup("")} />
    </>
  );
}

type ReservationLabelWrapperProps = {
  children?: React.ReactNode;
  label?: string;
  required?: boolean;
};

const ReservationLabelWrapper = ({
  children,
  label,
  required = true,
}: ReservationLabelWrapperProps) => {
  return (
    <div className="flex flex-col py-5 w-full gap-2.5">
      <CommonLabel label={label} required={required} />
      <div className="flex flex-col">{children}</div>
    </div>
  );
};

export const ClockIcon = () => (
  <svg
    width="36"
    height="36"
    viewBox="0 0 36 36"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M22.6191 20.7486L18 17.9929V12.0525"
      stroke="black"
      strokeWidth="1.5"
      strokeLinecap="square"
      strokeLinejoin="round"
    />
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M30.6001 30.6001L30.6001 5.3999L5.3999 5.3999L5.3999 30.6001L30.6001 30.6001Z"
      stroke="black"
      strokeWidth="1.5"
      strokeLinecap="round"
    />
  </svg>
);
